<template>
    <tailwind-layout :title="property.full_address">
        <div class="bg-white rounded-lg shadow-sm -mt-10 -mx-2">
            <div
                v-if="isSharedViewOnly && view_only_tooltip"
                class="flex items-center gap-3 bg-rent-risk-blue-100 px-6 py-3 text-sm mb-2 dark:bg-blue-900/20"
                role="status"
            >
                <font-awesome-icon :icon="faEye" class="h-4 w-4 shrink-0 text-rent-risk-blue-400 dark:text-blue-400" aria-hidden="true" />
                <span class="font-semibold text-rent-risk-blue-400 dark:text-blue-200">{{ view_only_tooltip.title }}.</span>
                <span class="text-gray-600 dark:text-gray-400">You can review this property, but only the listing agent can change settings or take owner actions.</span>
                <template v-if="assigned_by?.name || assigned_by?.email">
                    <span class="text-gray-300 dark:text-zinc-600">·</span>
                    <span class="text-gray-600 dark:text-gray-400">
                        Assigned by
                        <span class="font-semibold text-gray-800 dark:text-gray-200">{{ assigned_by.name }}</span>
                        <template v-if="assigned_by.email">
                            <span class="mx-1 text-gray-300 dark:text-zinc-600">·</span>
                            <span class="tabular-nums">{{ assigned_by.email }}</span>
                        </template>
                    </span>
                </template>
            </div>
            <div
                v-if="gracePeriodWarning?.show"
                class="flex flex-col gap-4 border-b border-amber-200 bg-amber-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-8"
                role="alert"
            >
                <div class="flex items-start gap-3 min-w-0">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                        <font-awesome-icon
                            :icon="faCircleExclamation"
                            class="h-5 w-5 text-amber-600"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="min-w-0">
                        <p class="text-sm font-semibold text-amber-950">
                            Your subscription has been canceled
                        </p>
                        <p class="mt-1 text-sm leading-relaxed text-amber-900">
                            Billable properties will be archived in
                            <span class="font-semibold tabular-nums">{{ gracePeriodWarning.days_remaining }}</span>
                            {{ gracePeriodWarning.days_remaining === 1 ? 'day' : 'days' }}
                            unless you restore your subscription.
                        </p>
                    </div>
                </div>
                <a
                    v-if="gracePeriodWarning.pricing_url"
                    :href="gracePeriodWarning.pricing_url"
                    class="inline-flex shrink-0 items-center justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 sm:ml-4"
                >
                    Restore subscription
                </a>
            </div>
            <div
                v-if="isApiPausedLocked"
                class="mx-6 mt-4 mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 lg:mx-8"
                role="status"
            >
                This property is paused. Activate it to manage settings, screening, and other actions.
            </div>
            <!-- Property Header Section -->
            <PropertyHeroBand :property="property" :property-access="property_access">
                <template #actions>
                    <span class="isolate inline-flex rounded-md shadow-sm">
                        <Link :href="route('properties.index')"
                              class="relative inline-flex items-center rounded-l-md bg-white/90 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 backdrop-blur hover:bg-white focus:z-10">
                            <font-awesome-icon :icon="faSquareChevronLeft" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Back</span>
                        </Link>
                        <PropertyLifecycleStatusDropdown
                            :property="property"
                            :options="propertyLifecycleStatuses"
                            :disabled="isSharedViewOnly"
                            :is-updating="isTogglingStatus"
                            :show-archive-action="!property.deleted_at && !isApiPausedLocked"
                            :show-restore-action="Boolean(property.deleted_at)"
                            @select="handleLifecycleStatusSelect"
                            @archive="showArchiveModal = true"
                            @restore="showRestoreModal = true"
                        />
                        <Link v-if="!isSharedViewOnly && !isApiPausedLocked" :href="route('properties.edit', property.id)"
                              class="relative -ml-px inline-flex items-center rounded-r-md bg-white/90 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 backdrop-blur hover:bg-white focus:z-10">
                            <font-awesome-icon :icon="faPenToSquare" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Edit</span>
                        </Link>
                        <span
                            v-else-if="!isSharedViewOnly && isApiPausedLocked"
                            class="relative -ml-px inline-flex items-center rounded-r-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400 ring-1 ring-inset ring-gray-300 cursor-not-allowed"
                        >
                            <font-awesome-icon :icon="faPenToSquare" class="text-gray-400" aria-hidden="true" />
                            <span class="ml-2">Edit</span>
                        </span>
                        <span
                            v-else
                            class="relative -ml-px inline-flex items-center rounded-r-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400 ring-1 ring-inset ring-gray-300 cursor-not-allowed"
                        >
                            <font-awesome-icon :icon="faPenToSquare" class="text-gray-400" aria-hidden="true" />
                            <span class="ml-2">Edit</span>
                        </span>
                    </span>
                    <ToolTip
                        v-if="isSharedViewOnly && view_only_tooltip"
                        :title="view_only_tooltip.title"
                        :description="view_only_tooltip.description"
                    />
                    <button
                        v-if="canReopenApplications"
                        type="button"
                        :disabled="isTogglingStatus"
                        data-test="reopen-applications-button"
                        class="relative inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:z-10 disabled:pointer-events-none disabled:opacity-50"
                        @click="showReopenApplicationsModal = true"
                    >
                        <font-awesome-icon :icon="faDoorOpen" class="mr-2" />
                        Reopen for Applications
                    </button>
                    <PropertyPlanActionGroup
                        :tier="property.deleted_at ? null : property.tier"
                        :can-upgrade="canUpgradeProperty"
                        :upgrade-label="upgradeCtaLabel"
                        :share-disabled="isSharedViewOnly || isApiPausedLocked"
                        @upgrade="openUpgradeFlow"
                        @share="openPropertyShareModal('socialShare')"
                    />
                </template>

                <template #details>
                    <div class="flex items-center gap-3">
                        <h1 class="text-3xl font-bold text-white drop-shadow sm:text-4xl">
                            {{ formattedPrice }}<span class="text-xl font-medium opacity-90 sm:text-2xl">/month</span>
                        </h1>
                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                            <PropertyLifecycleStatusBadge
                                :lifecycle-status="property.lifecycle_status"
                                :deleted-at="property.deleted_at"
                                variant="hero"
                            />
                        </span>
                    </div>
                    <p class="mt-1 text-base text-white/90 drop-shadow">{{ property.full_address }}</p>
                    <div v-if="property.sqft || formattedBedsBaths" class="mt-1 flex items-center gap-4 text-sm text-white/90 drop-shadow">
                        <span v-if="property.sqft">{{ property.sqft }} sq.ft</span>
                        <span v-if="formattedBedsBaths">{{ formattedBedsBaths }}</span>
                    </div>
                </template>
            </PropertyHeroBand>

            <!-- Price/address fallback when no cover photo (rendered below the hero band) -->
            <div v-if="!hasCoverPhoto" class="px-6 pt-6 lg:px-8">
                <div class="flex items-center gap-3">
                    <h1 class="text-3xl font-bold text-gray-900">
                        {{ formattedPrice }}/month
                    </h1>
                    <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                        <PropertyLifecycleStatusBadge
                            :lifecycle-status="property.lifecycle_status"
                            :deleted-at="property.deleted_at"
                        />
                    </span>
                </div>
                <p class="mt-1 text-base text-gray-600">{{ property.full_address }}</p>
                <div v-if="property.sqft || formattedBedsBaths" class="mt-1 flex items-center gap-4 text-sm text-gray-600">
                    <span v-if="property.sqft">{{ property.sqft }} sq.ft</span>
                    <span v-if="formattedBedsBaths">{{ formattedBedsBaths }}</span>
                </div>
            </div>

            <!-- Property Description and Amenities (50/50 on desktop, stacked on mobile) -->
            <div class="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:px-8">
                <PropertyShowDescriptionSection :property="property" :property-access="property_access" :view-only-tooltip="view_only_tooltip" />
                <PropertyShowAmenitiesSection :property="property" :amenity-options="amenityOptions" :property-access="property_access" :view-only-tooltip="view_only_tooltip" />
            </div>
            
            <!-- Navigation Tabs or Restore Message -->
            <div class="px-6 pt-4">
                <!-- Show restore message when archived -->
                <div v-if="property.deleted_at" class="py-6">
                    <div class="bg-red-50 border border-red-200 border-t-4 border-t-red-800 rounded-lg p-4 flex items-center justify-between">
                        <div class="flex items-center">
                            <font-awesome-icon :icon="faBoxArchive" class="text-red-700 mx-3" />
                            <div class="text-sm font-medium text-red-800">
                                This property has been archived. 
                                <button
                                    class="text-sm font-medium ml-1 underline text-red-800 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                                    type="button"
                                    :disabled="isSharedViewOnly"
                                    @click="showRestoreModal = true"
                                >
                                    Click here to Restore
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Show tabs when not archived -->
                <div v-else>
                    <div class="grid grid-cols-1 sm:hidden">
                        <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                        <select 
                            aria-label="Select a tab" 
                            :value="currentTab"
                            @change="handleTabChange($event.target.value)"
                            class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600"
                        >
                            <option v-for="tab in tabs" :key="tab.slug" :value="tab.slug">{{ tab.name }}</option>
                        </select>
                        <font-awesome-icon 
                            :icon="faChevronDown" 
                            class="pointer-events-none col-start-1 row-start-1 mr-2 h-5 w-5 self-center justify-self-end text-gray-500" 
                            aria-hidden="true" 
                        />
                    </div>
                    <div class="hidden sm:block">
                        <div class="border-b border-gray-200">
                            <!-- The full tab names (RAC-10476) no longer fit a laptop-width
                                 row, so the row scrolls sideways instead of overflowing. -->
                            <nav class="-mb-px flex gap-x-4 overflow-x-auto" aria-label="Tabs">
                                <button
                                    v-for="tab in tabs"
                                    :key="tab.slug"
                                    @click="handleTabChange(tab.slug)"
                                    :class="[
                                        currentTab === tab.slug
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                        'group inline-flex shrink-0 items-center whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium cursor-pointer'
                                    ]"
                                    :aria-current="currentTab === tab.slug ? 'page' : undefined"
                                >
                                    <font-awesome-icon 
                                        :icon="tab.icon" 
                                        :class="[
                                            currentTab === tab.slug
                                                ? 'text-blue-500'
                                                : 'text-gray-400 group-hover:text-gray-500',
                                            '-ml-0.5 mr-1.5 h-5 w-5'
                                        ]"
                                        aria-hidden="true"
                                    />
                                    <span>{{ tab.name }}</span>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Content Area -->
            <div v-show="!property.deleted_at" class="p-6">
                <div
                    v-if="isApiPausedLocked"
                    class="rounded-lg border border-amber-200 bg-amber-50 px-6 py-8 text-center"
                    role="status"
                >
                    <p class="text-base font-semibold text-amber-900">
                        This property is paused
                    </p>
                    <p class="mt-2 text-sm text-amber-800 max-w-xl mx-auto">
                        Activate this property using the button above to manage
                        {{ currentTabLabel.toLowerCase() }} and other property settings.
                    </p>
                </div>
                <div v-else>
                    <component
                    :is="currentTabComponent"
                    ref="currentTabRef"
                    :property="property"
                    :tab="tab"
                    :tab-data="tabData"
                    :addons="addons"
                    :has-protect-access="hasProtectAccess"
                    :is-screening-only="isScreeningOnly"
                    :subscription-type="subscriptionType"
                    :free-bundles-used="freeBundlesUsed"
                    :free-eso-offer-used="freeEsoOfferUsed"
                    :is-user-experian-verified="isUserExperianVerified"
                    :is-new-agent="isNewAgent"
                    :protect-tos-accepted-at="protectTosAcceptedAt"
                    :is-agent-payment-method-added="isAgentPaymentMethodAdded"
                    :user-payment-method="userPaymentMethod"
                    :stripe-data="stripeData"
                    :subscription-protection-ref="subscriptionProtectionRef"
                    :user-global-auto-eso="user_global_auto_eso"
                    :property-access="property_access"
                    :assigned-by="assigned_by"
                    :view-only-tooltip="view_only_tooltip"
                    @subscription-success="handleSubscriptionSuccess"
                    @subscription-error="handleSubscriptionError"
                    @subscription-cancel="handleSubscriptionCancel"
                    @request-add-card="openAddCardModal"
                    @request-share-modal="openPropertyShareModal('screeningLink')"
                    @request-upgrade="openUpgradeFlow"
                    />
                </div>
            </div>
        </div>
        
        <!-- Archive Modal -->
        <ArchivePropertyModal
            :show="showArchiveModal"
            @close="showArchiveModal = false"
            @confirm="destroy()"
        />

        <!-- 86bbrwd3v: explicit reopen after an approved applicant moved on -->
        <ReopenApplicationsModal
            :show="showReopenApplicationsModal"
            :processing="isTogglingStatus"
            @close="showReopenApplicationsModal = false"
            @confirm="confirmReopenApplications"
        />

        <!-- 86bcd9hg3: the account-wide Pro pop-up (shows "Switch to new plan" itself for
             landlords on an earlier plan). -->
        <RiskGuardProUpsellModal
            :show="showUpgradeProModal"
            :processing="tierProcessing"
            :stripe-data="isValidStripeData ? normalizedStripeData : null"
            :user-payment-method="userPaymentMethod"
            :subscription="subscriptionProtectionRef"
            :show-features="true"
            :notice="tierChangedNotice"
            @dismiss="closeUpgradeProModal"
            @subscribe="handleSubscribeStart"
        />

        <!-- 86bcd9hg3 (decision 6): restoring or first-activating a property that moves an
             account-wide Pro landlord up a tier shows the new price before going ahead. -->
        <ProTierChangeNoticeModal
            :show="showTierChangeNotice"
            :processing="isTogglingStatus"
            :change="nextPropertyTierChange ?? undefined"
            @close="cancelTierChangeNotice"
            @confirm="confirmTierChangeNotice"
        />

        <!-- Restore Modal -->
        <RestorePropertyModal 
            :show="showRestoreModal"
            @close="showRestoreModal = false"
            @confirm="restore()"
        />
        
        <!-- Share Property Modal -->
        <ShareModal 
            :show="showPropertyShareModal"
            :share-url="property.application_link"
            :share-text="shareText"
            :title="shareModalTitle"
            :link-only="shareModalLinkOnly"
            @close="closePropertyShareModal"
        />
        
        <!-- Subscription Protection Component -->
        <SubscriptionProtection
            ref="subscriptionProtectionRef"
            :subscription-type="'protect'"
            :terms-accepted="protectTosAcceptedAt"
            :has-payment-method="isAgentPaymentMethodAdded"
            :user-payment-method="userPaymentMethod"
            :stripe-data="stripeData"
            :show-confirmation-modal="showConfirmationForSubscription"
            @success="handleSubscriptionSuccess"
            @error="handleSubscriptionError"
            @cancel="handleSubscriptionCancel"
            @tier-changed="handleTierChanged"
        />
        
        <!-- Shared Add Card Modal. Callers may pass a context payload via
             request-add-card to give the modal purchase-specific copy (e.g. the
             $29 enhanced-screening charge); absent a context it falls back to
             the generic subscription card-on-file copy. -->
        <AddCardPopup
            v-if="isValidStripeData"
            :key="normalizedStripeData?.publishable_key"
            :show="showAddCardModal"
            :stripe_data="normalizedStripeData"
            :heading="addCardContext?.heading"
            :notice-text="addCardContext?.noticeText"
            :confirm-button-text="addCardContext?.confirmButtonText"
            @card-added="handleCardAdded"
            @close="closeCardModal"
        />

        <ApiPausedPropertyBillingAckModal
            :show="showApiBillingAckModal"
            :processing="isTogglingStatus"
            :requires-multi-property-ack="pendingMultiPropertyAck"
            :requires-per-property-ack="pendingPerPropertyAck"
            :per-property-price-formatted="billingAckPriceFormatted"
            @confirm="confirmApiPausedActivation"
            @cancel="closeApiBillingAckModal"
        />

        <SubscriptionGracePeriodWarningModal
            :warning="gracePeriodWarning"
        />
    </tailwind-layout>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3'
import axios from 'axios'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxArchive, faPenToSquare, faSquareChevronLeft, faEye, faDoorOpen } from '@fortawesome/pro-solid-svg-icons'
import { faChevronDown, faFileContract, faCommentsDollar, faScreenUsers, faWrench, faHouseWater, faSquareDollar, faFolderOpen, faCircleExclamation } from '@fortawesome/pro-light-svg-icons'
import ShareModal from './ShareModal.vue'
import ArchivePropertyModal from './ArchivePropertyModal.vue'
import ReopenApplicationsModal from './ReopenApplicationsModal.vue'
import RestorePropertyModal from './RestorePropertyModal.vue'
import TenantScreeningTab from './Tabs/TenantScreeningTab.vue'
import LeaseAgreementTab from './Tabs/LeaseAgreementTab.vue'
import RentPaymentsTab from './Tabs/RentPaymentsTab.vue'
import MaintenanceTab from './Tabs/MaintenanceTab.vue'
import LandlordInsuranceTab from './Tabs/LandlordInsuranceTab.vue'
import RefinanceTab from './Tabs/RefinanceTab.vue'
import DocumentsTab from './Tabs/DocumentsTab.vue'
import SubscriptionProtection from '@/Shared/V2/SubscriptionProtection.vue'
import AddCardPopup from '@/Shared/AddCardPopup.vue'
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue'
import ApiPausedPropertyBillingAckModal from '@/Components/ApiPausedPropertyBillingAckModal.vue'
import ProTierChangeNoticeModal from '@/Components/ProTierChangeNoticeModal.vue'
import { proCta } from '@/Shared/proPlanState'
import SubscriptionGracePeriodWarningModal from '@/Components/SubscriptionGracePeriodWarningModal.vue'
import PropertyHeroBand from './Components/PropertyHeroBand.vue'
import PropertyLifecycleStatusBadge from './Components/PropertyLifecycleStatusBadge.vue'
import PropertyPlanActionGroup from './Components/PropertyPlanActionGroup.vue'
import PropertyLifecycleStatusDropdown from './Components/PropertyLifecycleStatusDropdown.vue'
import PropertyShowDescriptionSection from './PropertyShowDescriptionSection.vue'
import PropertyShowAmenitiesSection from './PropertyShowAmenitiesSection.vue'
import ToolTip from '@/Shared/ToolTip.vue'

// Route function is available globally via Ziggy
const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    // Fallback for route function
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    tab: {
        type: String,
        default: 'tenant-screening'
    },
    tabData: {
        type: Object,
        default: () => ({})
    },
    addons: {
        type: Array,
        default: () => []
    },
    hasProtectAccess: {
        type: Boolean,
        default: false
    },
    subscriptionType: {
        type: String,
        default: null
    },
    freeBundlesUsed: {
        type: Number,
        default: 0
    },
    freeEsoOfferUsed: {
        type: Boolean,
        default: false
    },
    isUserExperianVerified: {
        type: Boolean,
        default: false
    },
    is_new_agent: {
        type: Boolean,
        default: false
    },
    protect_tos_accepted_at: {
        type: Boolean,
        default: false
    },
    is_agent_payment_method_added: {
        type: Boolean,
        default: false
    },
    user_payment_method: {
        type: Object,
        default: null
    },
    stripe_data: {
        type: [Object, Array],
        default: () => []
    },
    user_global_auto_eso: {
        type: Object,
        default: () => ({
            is_active: false,
            payer: null,
            effective_billing: false,
            payment_method_required_but_missing: false
        })
    },
    amenityOptions: {
        type: Object,
        default: () => ({})
    },
    property_access: {
        type: String,
        default: 'owner'
    },
    assigned_by: {
        type: Object,
        default: null
    },
    view_only_tooltip: {
        type: Object,
        default: null
    },
    api_paused_property_locked: {
        type: Boolean,
        default: false
    },
    paused_api_activation: {
        type: Object,
        default: () => ({
            applies: false,
            requires_subscription: false,
            requires_multi_property_ack: false,
            requires_per_property_ack: false,
            per_property_price_formatted: null,
            is_protect_pro: false,
        })
    },
    subscription_grace_period_warning: {
        type: Object,
        default: () => ({
            show: false,
            days_remaining: 0,
            grace_period_ends_at: null,
            pricing_url: null,
        }),
    },
    property_lifecycle_statuses: {
        type: Array,
        default: () => [],
    },
})

const page = usePage()

const gracePeriodWarning = computed(() => {
    return page.props.subscription_grace_period_warning
        ?? props.subscription_grace_period_warning
        ?? { show: false, days_remaining: 0 }
})

const currentTab = computed(() => {
    return props.tab || 'tenant-screening'
})

const tabs = [
    { name: 'Tenant Screening', icon: faScreenUsers, slug: 'tenant-screening', component: TenantScreeningTab },
    { name: 'Lease Agreements', icon: faFileContract, slug: 'lease-agreement', component: LeaseAgreementTab },
    { name: 'Rent Collection', icon: faCommentsDollar, slug: 'rent-payments', component: RentPaymentsTab },
    { name: 'Maintenance', icon: faWrench, slug: 'maintenance', component: MaintenanceTab },
    { name: 'Landlord Insurance', icon: faHouseWater, slug: 'landlord-insurance', component: LandlordInsuranceTab },
    { name: 'Refinance My Property', icon: faSquareDollar, slug: 'refinance', component: RefinanceTab },
    { name: 'Documents', icon: faFolderOpen, slug: 'documents', component: DocumentsTab },
]

const currentTabComponent = computed(() => {
    const activeTab = tabs.find(t => t.slug === currentTab.value)
    return activeTab ? activeTab.component : TenantScreeningTab
})

const currentTabLabel = computed(() => {
    const activeTab = tabs.find(t => t.slug === currentTab.value)
    return activeTab ? activeTab.name : 'Tenant Screening'
})

const clearFlashMessages = () => {
    if (page?.props?.flash) {
        page.props.flash.success = null
        page.props.flash.error = null
    }
}

const setFlashSuccess = (message) => {
    if (! page?.props?.flash) {
        return
    }

    page.props.flash.success = message || 'Successfully subscribed to RiskGuard Pro!'
}

const setFlashError = (message) => {
    if (! page?.props?.flash || ! message) {
        return
    }

    page.props.flash.error = message
}

const isSharedViewOnly = computed(() => props.property_access === 'shared_view')
const isApiPausedLocked = computed(() => page.props.api_paused_property_locked ?? props.api_paused_property_locked)
const pausedApiActivation = computed(() => page.props.paused_api_activation ?? props.paused_api_activation)
const hasProtectAccessNow = computed(() => page.props.hasProtectAccess ?? props.hasProtectAccess)

const viewOnlyBannerPlainText = computed(() => {
    if (! props.view_only_tooltip?.description) {
        return ''
    }

    const tmp = props.view_only_tooltip.description.replace(/<[^>]*>/g, ' ')

    return tmp.replace(/\s+/g, ' ').trim()
})

const handleTabChange = (tabSlug) => {
    clearFlashMessages()
    router.get(route('properties.show', props.property.id), { tab: tabSlug }, { 
        preserveState: true, 
        preserveScroll: true,
        only: ['tabData', 'tab', 'user_global_auto_eso', 'property_access', 'assigned_by', 'view_only_tooltip'],
    })
}

const formattedPrice = computed(() => {
    return props.property.rent
        ? `$${Number(props.property.rent).toLocaleString()}`
        : 'N/A'
})

const hasCoverPhoto = computed(() => Boolean(props.property.photo))

const formattedBedsBaths = computed(() => {
    const beds = props.property.beds ?? null
    const baths = props.property.baths ?? null
    
    if (beds === null && baths === null) return null
    
    const bedsStr = beds !== null ? `${beds} bed${beds !== 1 ? 's' : ''}` : null
    const bathsStr = baths !== null ? `${baths} bath${baths !== 1 ? 's' : ''}` : null
    
    if (bedsStr && bathsStr) {
        return `${bedsStr}/${bathsStr}`
    } else if (bedsStr) {
        return bedsStr
    } else if (bathsStr) {
        return bathsStr
    }
    
    return null
})

/** Labels and ShareModal wiring per entry point — adjust copy here without changing handlers. */
const propertyShareModalPresets = {
    socialShare: {
        linkOnly: false,
        title: 'Share Property Application Link',
    },
    screeningLink: {
        linkOnly: true,
        title: 'Start Screening Tenants',
    },
}

// Modal states
const showArchiveModal = ref(false)
const showReopenApplicationsModal = ref(false)

// 86bcd9hg3: account-wide Pro. The header button and the tabs' upgrade prompts all open
// one pop-up; it charges the tier for the landlord's property count, or offers "Switch to
// new plan" to a landlord on an earlier plan.
const showUpgradeProModal = ref(false)
const tierProcessing = ref(false)
// True while a Pro purchase started from this page's own pop-up is in flight; otherwise
// a tab started it, and a price change goes back to that tab's pop-up.
const purchaseStartedHere = ref(false)
const proPlan = computed(() => page.props.auth?.user?.pro_plan ?? null)
const nextPropertyTierChange = computed(() => proPlan.value?.next_property_tier_change ?? null)

const openUpgradeFlow = () => {
    if (proCta(proPlan.value) === 'fix-card') {
        router.visit(route('payment-methods.index'))
        return
    }

    showUpgradeProModal.value = true
}

// Why the pop-up reopened (the price moved before the charge); shown inside it.
const tierChangedNotice = ref(null)

const closeUpgradeProModal = () => {
    showUpgradeProModal.value = false
    tierChangedNotice.value = null
}

// 86bc9e403: the pop-up is the combined in-place checkout — it stores the plan, saves a card
// if needed and subscribes itself through the SubscriptionProtection ref. This only tracks
// that the purchase started from this page's own pop-up (not a tab's), so a price change
// reopens the right one.
const handleSubscribeStart = () => {
    tierProcessing.value = true
    purchaseStartedHere.value = true
}

// The price moved since the pop-up (a property was added in between): nothing was
// charged. Refresh the quote and show the pop-up that started the purchase again — a
// tab's own pop-up keeps its "pay $49 / $29 instead" option.
const handleTierChanged = ({ message }) => {
    tierProcessing.value = false
    const startedHere = purchaseStartedHere.value
    purchaseStartedHere.value = false

    router.reload({
        only: ['auth'],
        preserveScroll: true,
        onSuccess: () => {
            const tab = currentTabRef.value
            if (! startedHere && tab && typeof tab.handleTierChanged === 'function') {
                tab.handleTierChanged(message)
                return
            }

            tierChangedNotice.value = message || null
            showUpgradeProModal.value = true
        },
    })
}

// Decision 6: before restoring or first-activating a property that moves an account-wide
// Pro landlord up a tier, show the new price (charged at renewal) and let them back out.
const showTierChangeNotice = ref(false)
const pendingTierChangeAction = ref(null)

const withTierChangeNotice = (action) => {
    if (nextPropertyTierChange.value) {
        pendingTierChangeAction.value = action
        showTierChangeNotice.value = true
        return
    }

    action()
}

const confirmTierChangeNotice = () => {
    const action = pendingTierChangeAction.value
    showTierChangeNotice.value = false
    pendingTierChangeAction.value = null
    action?.()
}

const cancelTierChangeNotice = () => {
    showTierChangeNotice.value = false
    pendingTierChangeAction.value = null
}
const showRestoreModal = ref(false)
const showPropertyShareModal = ref(false)
const shareModalLinkOnly = ref(false)
const shareModalTitle = ref(propertyShareModalPresets.socialShare.title)

const openPropertyShareModal = (presetKey) => {
    if (isSharedViewOnly.value) {
        return
    }
    const preset = propertyShareModalPresets[presetKey]
    if (!preset) {
        return
    }

    shareModalLinkOnly.value = preset.linkOnly
    shareModalTitle.value = preset.title
    showPropertyShareModal.value = true
}

const closePropertyShareModal = () => {
    showPropertyShareModal.value = false
}

const ACTIVE_APPLICATION_LINK = 1
const PAUSED_APPLICATION_LINK = 2

const propertyLifecycleStatuses = computed(() => {
    return page.props.property_lifecycle_statuses?.length
        ? page.props.property_lifecycle_statuses
        : props.property_lifecycle_statuses
})

const PENDING_API_PAUSED_ACTIVATION_KEY = 'pending_api_paused_activation_property_id'
const ACTIVATION_CONTEXT_KEY = 'pending_api_paused_activation_context'

const isTogglingStatus = ref(false)
const showApiBillingAckModal = ref(false)
const pendingMultiPropertyAck = ref(false)
const pendingPerPropertyAck = ref(false)
const billingAckPriceFormatted = ref(null)
const pendingLifecycleStatus = ref(null)

const clearPendingApiPausedActivation = () => {
    try {
        sessionStorage.removeItem(PENDING_API_PAUSED_ACTIVATION_KEY)
    } catch (e) {
        console.error(e)
    }
}

const cacheActivationContext = (force = false) => {
    const activation = resolvePausedApiActivationContext() ?? {}

    if (! force && ! activation?.applies) {
        return
    }

    try {
        sessionStorage.setItem(ACTIVATION_CONTEXT_KEY, JSON.stringify({
            propertyId: String(props.property.id),
            requires_multi_property_ack: force || Boolean(activation.requires_multi_property_ack),
            requires_per_property_ack: Boolean(activation.requires_per_property_ack),
            per_property_price_formatted: activation.per_property_price_formatted ?? null,
        }))
    } catch (e) {
        console.error(e)
    }
}

const getCachedActivationContext = () => {
    try {
        const raw = sessionStorage.getItem(ACTIVATION_CONTEXT_KEY)

        if (! raw) {
            return null
        }

        const parsed = JSON.parse(raw)

        if (parsed?.propertyId !== String(props.property.id)) {
            return null
        }

        return parsed
    } catch (e) {
        return null
    }
}

const clearCachedActivationContext = () => {
    try {
        sessionStorage.removeItem(ACTIVATION_CONTEXT_KEY)
    } catch (e) {
        console.error(e)
    }
}

const resolvePausedApiActivationContext = () => {
    return page.props.paused_api_activation ?? props.paused_api_activation
}

const buildEffectiveActivationContext = () => {
    const server = resolvePausedApiActivationContext() ?? {}
    const cached = getCachedActivationContext()

    if (! cached) {
        return server
    }

    return {
        ...server,
        applies: server.applies ?? isApiPausedLocked.value,
        requires_multi_property_ack: Boolean(cached?.requires_multi_property_ack) || Boolean(server.requires_multi_property_ack),
        requires_per_property_ack: Boolean(cached?.requires_per_property_ack) || Boolean(server.requires_per_property_ack),
        per_property_price_formatted: cached?.per_property_price_formatted ?? server.per_property_price_formatted,
    }
}

const fetchFreshActivationContext = async () => {
    const { data } = await axios.get(route('properties.paused-api-activation-context', props.property.id))

    return data
}

const applyFreshActivationContext = (data) => {
    if (! page?.props) {
        return
    }

    page.props.hasProtectAccess = data.has_protect_access
    page.props.api_paused_property_locked = data.api_paused_property_locked
    page.props.paused_api_activation = data.paused_api_activation
}

const resolveActivationForContinue = (freshData = null) => {
    if (freshData?.paused_api_activation) {
        return {
            ...freshData.paused_api_activation,
            applies: freshData.paused_api_activation.applies ?? freshData.api_paused_property_locked,
        }
    }

    return buildEffectiveActivationContext()
}

const openBillingAckModalFromActivation = (activation) => {
    billingAckPriceFormatted.value = activation.per_property_price_formatted ?? null

    if (activation.requires_multi_property_ack) {
        pendingMultiPropertyAck.value = true
        pendingPerPropertyAck.value = Boolean(activation.requires_per_property_ack)
        showApiBillingAckModal.value = true
        return true
    }

    if (activation.requires_per_property_ack) {
        pendingMultiPropertyAck.value = false
        pendingPerPropertyAck.value = true
        showApiBillingAckModal.value = true
        return true
    }

    return false
}

const continuePausedApiPropertyActivation = async () => {
    let freshData = null

    if (isApiPausedLocked.value) {
        try {
            freshData = await fetchFreshActivationContext()
            applyFreshActivationContext(freshData)
        } catch (e) {
            console.error(e)
        }
    }

    const activation = resolveActivationForContinue(freshData)

    if (! activation?.applies) {
        submitLifecycleStatusUpdate({
            lifecycle_status: pendingLifecycleStatus.value ?? ACTIVE_APPLICATION_LINK,
        })
        return
    }

    // 86bcd9hg3: only a legacy RiskGuard plan with a failed payment lands here (its
    // per-property billing needs a working subscription). Buying Pro on top isn't
    // possible, so point them at fixing the card instead.
    if (activation.requires_subscription && ! hasProtectAccessNow.value) {
        clearActivationFlowState()
        setFlashError(`Your RiskGuard plan has a payment problem. <a href="${route('payment-methods.index')}" class="underline">Update your card on the Billing page</a>, then activate this property.`)
        return
    }

    if (openBillingAckModalFromActivation(activation)) {
        return
    }

    submitLifecycleStatusUpdate({
        lifecycle_status: pendingLifecycleStatus.value ?? ACTIVE_APPLICATION_LINK,
    })
}

const clearActivationFlowState = () => {
    clearPendingApiPausedActivation()
    clearCachedActivationContext()
}

// Subscription protection refs and state
const subscriptionProtectionRef = ref(null)
const currentTabRef = ref(null)
const showConfirmationForSubscription = ref(false)

// Shared add card modal state
const showAddCardModal = ref(false)
// Purchase-specific copy for the shared add-card modal; null → generic copy.
const addCardContext = ref(null)

// Computed properties for easier prop access
const protectTosAcceptedAt = computed(() => props.protect_tos_accepted_at)
const isAgentPaymentMethodAdded = computed(() => props.is_agent_payment_method_added)
const isNewAgent = computed(() => props.is_new_agent)
// Read from page.props first so an in-place subscribe (which reloads
// getSubscriptionProps() into page.props, not the initial props) flips the
// lease tab's Get E-Sign / Book a Call gating without a manual refresh — same
// pattern as hasProtectAccessNow above (RAC-10254).
const hasProtectAccess = computed(() => page.props.hasProtectAccess ?? props.hasProtectAccess)
const isScreeningOnly = computed(() => page.props.is_screening_only ?? props.property?.is_screening_only ?? false)


const subscriptionType = computed(() => page.props.subscriptionType ?? props.subscriptionType)
const freeBundlesUsed = computed(() => props.freeBundlesUsed)
const freeEsoOfferUsed = computed(() => props.freeEsoOfferUsed)
const isUserExperianVerified = computed(() => props.isUserExperianVerified)
const addons = computed(() => props.addons)
const userPaymentMethod = computed(() => props.user_payment_method)
const stripeData = computed(() => props.stripe_data)

// Share text
const shareText = computed(() => {
    const street = [props.property.address_1, props.property.address_2].filter(Boolean).join(' ')
    return `${street || props.property.full_address} is available for rent. Apply here today!`
})

// Methods
const destroy = () => {
    router.delete(route('properties.destroy', props.property.id), {
        onFinish: () => {
            showArchiveModal.value = false
        }
    })
}

const restore = () => {
    showRestoreModal.value = false
    const putRestore = () => router.put(route('properties.restore', props.property.id))

    // A partner-API property that was never activated doesn't count toward the Pro
    // tier, so restoring it can't change the price.
    if (props.property.is_api_property && props.property.api_pending_first_activation) {
        putRestore()
        return
    }

    withTierChangeNotice(putRestore)
}

const handleLifecycleStatusSelect = (lifecycleStatus) => {
    if (isTogglingStatus.value) {
        return
    }

    pendingLifecycleStatus.value = lifecycleStatus

    if (
        lifecycleStatus !== PAUSED_APPLICATION_LINK
        && isApiPausedLocked.value
    ) {
        cacheActivationContext(true)
        void continuePausedApiPropertyActivation()
        return
    }

    // Decision 6: a partner-API property's first activation adds it to the Pro tier count.
    if (lifecycleStatus !== PAUSED_APPLICATION_LINK && props.property.is_api_property && props.property.api_pending_first_activation) {
        withTierChangeNotice(() => submitLifecycleStatusUpdate({ lifecycle_status: lifecycleStatus }))
        return
    }

    submitLifecycleStatusUpdate({ lifecycle_status: lifecycleStatus })
}

// 86bc2cgxd: a screening-only property offers Upgrade Property in the hero
// instead of Share — the screening link stays on the Tenant Screening tab.
// 86bcd9hg3: the button follows the shared Pro rule — hidden once on Pro, "Update card"
// after a failed payment.
const canUpgradeProperty = computed(() =>
    isScreeningOnly.value
    && proCta(proPlan.value) !== null
    && !props.property.deleted_at
    && !isSharedViewOnly.value
)

const upgradeCtaLabel = computed(() => (proCta(proPlan.value) === 'fix-card' ? 'Update card' : 'Upgrade to Pro'))

// 86bbrwd3v: only an approved applicant gets the reopen button — paused and archived already
// have their own controls in the lifecycle dropdown. (86bc2cgxd dropped the landlord-facing
// "Not Available" badge; applicants still see it on the public page.)
const canReopenApplications = computed(() =>
    props.property.applications_closed_reason === 'approved_applicant'
    && !props.property.deleted_at
    && !isSharedViewOnly.value
)

const confirmReopenApplications = () => {
    handleLifecycleStatusSelect(ACTIVE_APPLICATION_LINK)
}

const submitLifecycleStatusUpdate = (payload) => {
    isTogglingStatus.value = true

    router.put(route('properties.update-lifecycle-status', props.property.id), payload, {
        preserveScroll: true,
        onSuccess: () => {
            clearActivationFlowState()
            pendingLifecycleStatus.value = null
            showReopenApplicationsModal.value = false
        },
        onFinish: () => {
            isTogglingStatus.value = false
            showApiBillingAckModal.value = false
            pendingMultiPropertyAck.value = false
            pendingPerPropertyAck.value = false
            billingAckPriceFormatted.value = null
        },
    })
}

const closeApiBillingAckModal = () => {
    showApiBillingAckModal.value = false
    pendingMultiPropertyAck.value = false
    pendingPerPropertyAck.value = false
    billingAckPriceFormatted.value = null
}

const confirmApiPausedActivation = () => {
    submitLifecycleStatusUpdate({
        lifecycle_status: pendingLifecycleStatus.value ?? ACTIVE_APPLICATION_LINK,
        api_paused_property_billing_acknowledged: pendingMultiPropertyAck.value,
        per_property_billing_acknowledged: pendingPerPropertyAck.value,
    })
}

// Subscription event handlers (forward to tab if needed)
const handleSubscriptionSuccess = (data) => {
    tierProcessing.value = false
    purchaseStartedHere.value = false
    // 86bc9e403: the combined checkout stays open through the subscribe, so close it on success.
    closeUpgradeProModal()

    if (data?.successMessage) {
        setFlashSuccess(data.successMessage)
    }

    if (currentTabRef.value && typeof currentTabRef.value.handleSubscriptionSuccess === 'function') {
        currentTabRef.value.handleSubscriptionSuccess(data)
        return
    }
}

const handleSubscriptionError = (data) => {
    tierProcessing.value = false
    const startedHere = purchaseStartedHere.value
    purchaseStartedHere.value = false
    clearPendingApiPausedActivation()

    // 86bc9e403 #1: a purchase that started from this page's own Pro pop-up re-enables its
    // Subscribe button (tierProcessing cleared above) and shows the reason inside it via the
    // notice. A tab's own upsell keeps handling its own error ($49 / $29 paths).
    if (startedHere) {
        tierChangedNotice.value = data?.error || 'We could not start your subscription. Please try again.'
        return
    }

    if (currentTabRef.value && typeof currentTabRef.value.handleSubscriptionError === 'function') {
        currentTabRef.value.handleSubscriptionError(data)
    }
}

const handleSubscriptionCancel = () => {
    tierProcessing.value = false
    purchaseStartedHere.value = false
    clearActivationFlowState()

    if (currentTabRef.value && typeof currentTabRef.value.handleSubscriptionCancel === 'function') {
        currentTabRef.value.handleSubscriptionCancel()
    }
}

// Computed properties for normalized stripe data
const normalizedStripeData = computed(() => {
    const data = stripeData.value
    if (Array.isArray(data) || !data || typeof data !== 'object') {
        return null
    }
    return data.publishable_key ? data : null
})

const isValidStripeData = computed(() => {
    return normalizedStripeData.value !== null && 
           normalizedStripeData.value.publishable_key && 
           normalizedStripeData.value.setup_intent
})

// Add Card Modal handlers
const handleCardAdded = ({ payment_method, success }) => {
    if (!success) {
        alert('Failed to add card. Please try again.')
        return
    }

    showAddCardModal.value = false
    addCardContext.value = null

    router.reload({
        only: ['tabData', 'user_payment_method', 'is_agent_payment_method_added'],
        preserveScroll: true
    })
}

const closeCardModal = () => {
    showAddCardModal.value = false
    addCardContext.value = null
}

const openAddCardModal = (context = null) => {
    if (isValidStripeData.value) {
        // Guard against a stray DOM event slipping in as the context arg.
        addCardContext.value = context && typeof context === 'object' && !(context instanceof Event)
            ? context
            : null
        showAddCardModal.value = true
    } else {
        alert('Unable to add card. Please refresh the page and try again.')
    }
}

</script>

